/**
 * useCategoryRail.js - Custom hook for fetching category channels with lazy loading
 *
 * This hook provides category channels data fetching functionality using the new
 * authentication system and React 19 patterns with enhanced error handling and retry logic.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { formatApiError } from '../utils/ApiUtils';
import spotlightService from '../services/SpotlightService';

// API response schema for validation
const channelSchema = {
  _id: { type: 'string', required: true },
  title: { type: 'string', required: true },
};

/**
 * Hook for fetching and managing category channels with lazy loading
 *
 * @param {Object} category - The category object
 * @param {boolean} isVisible - Whether the rail is visible in viewport
 * @param {string} countryCode - The country code for API requests (default: 'US')
 * @returns {Object} Category channels data and state
 * @returns {Array} .channels - Channels for the category
 * @returns {boolean} .loading - Whether data is currently loading
 * @returns {string} .error - Error message if fetch failed
 * @returns {Function} .retry - Function to retry fetching data
 */
export const useCategoryRail = (category, isVisible, countryCode = 'US') => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  const hasAttemptedFetch = useRef(false);

  // Enhanced mock data for development when API fails
  const mockChannels = Array(8)
    .fill()
    .map((_, i) => ({
      _id: `mock-rail-${i}`,
      title: `Sample ${i % 3 === 0 ? 'Movie' : i % 3 === 1 ? 'Series' : 'Documentary'} ${i + 1}`,
      spotlight_poster: `https://via.placeholder.com/300x450?text=Sample+${
        i % 3 === 0 ? 'Movie' : i % 3 === 1 ? 'Series' : 'Documentary'
      }+${i + 1}`,
      poster: `https://via.placeholder.com/300x450?text=Sample+${
        i % 3 === 0 ? 'Movie' : i % 3 === 1 ? 'Series' : 'Documentary'
      }+${i + 1}`,
      year: `${2020 + (i % 5)}`,
      duration: `${Math.floor(60 + Math.random() * 90)}m`,
      genres: [
        ['Action', 'Drama', 'Thriller', 'Comedy', 'Romance'][i % 5],
        ['Adventure', 'Sci-Fi', 'Horror', 'Family', 'Documentary'][Math.floor(i / 3) % 5],
      ],
      rating: `${Math.floor(6 + Math.random() * 4)}/10`,
      description: `This is a sample ${
        i % 3 === 0 ? 'movie' : i % 3 === 1 ? 'series' : 'documentary'
      } description for development purposes.`,
    }));

  // Main data fetching function with enhanced error handling and retry logic
  const fetchChannels = useCallback(async () => {
    // Don't fetch if no category, already loaded, loading, or not visible
    if (!category || channels.length > 0 || loading || !isVisible) {
      return;
    }

    // If already attempted fetch, don't try again automatically
    if (hasAttemptedFetch.current) {
      return;
    }

    // Mark that we've attempted a fetch
    hasAttemptedFetch.current = true;

    // If category already has channels array, use it
    if (Array.isArray(category.channels) && category.channels.length > 0) {
      setChannels(category.channels);

      // Store debug info
      setDebugInfo({
        channelsSource: 'category_object',
        channelsCount: category.channels.length,
      });

      return;
    }

    try {
      setLoading(true);
      setError(null);

      // We need the category slug to fetch channels using the correct endpoint
      if (!category.slug) {
        console.error(
          `useCategoryRail: Missing category slug for "${category.name}" (ID: ${category._id})`,
        );
        throw new Error(`Missing category slug for "${category.name}"`);
      }

      // Use the correct endpoint format with country code and slug
      // This matches the pattern used in useCategoryDetails.js
      const data = await spotlightService.getCategoryChannelsBySlug(category.slug).catch(e => {
        throw new Error(e);
      });

      const channelsData = data.channels;

      // Store debug info
      setDebugInfo({
        channelsSource: 'slug_object_format',
        channelsCount: data.channels.length,
        categorySlug: category.slug,
        countryCode,
      });

      setChannels(channelsData);
    } catch (err) {
      console.error(`useCategoryRail: Error loading channels for "${category.name}":`, err);

      // Format error message for user display
      const userFriendlyError = `Failed to load channels: ${err.message}`;
      setError(userFriendlyError);

      // Store debug info
      setDebugInfo({
        error: err.message,
        userFriendlyError,
        categoryId: category._id,
        categoryName: category.name,
        categorySlug: category.slug,
        usingMockData: true,
      });

      // Fall back to mock data for development
      setChannels(mockChannels);
    } finally {
      setLoading(false);
    }
  }, [category, channels.length, loading, isVisible, mockChannels, countryCode]);

  // Function to retry fetching data
  const retry = useCallback(() => {
    hasAttemptedFetch.current = false;
    fetchChannels();
  }, [fetchChannels]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  return {
    channels,
    loading,
    error,
    debugInfo,
    retry,
  };
};

export default useCategoryRail;
